import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabaseClient';
import { config } from '../../lib/config';
import { demoProfile } from '../demo/store';
import type { ProfileRow } from '../../types/db';

interface AuthValue {
  user: User | null;
  profile: ProfileRow | null;
  loading: boolean;
  isGuest: boolean;
  showOnboarding: boolean;
  login(codename: string, password: string): Promise<void>;
  register(codename: string, password: string, invite: string): Promise<void>;
  continueAsGuest(): void;
  finishOnboarding(): void;
  logout(): Promise<void>;
}

export const AuthContext = createContext<AuthValue | null>(null);
const emailFor = (codename: string): string => `${codename.toLowerCase()}@zenith.local`;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<ProfileRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [isGuest, setGuest] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const loadProfile = useCallback(async (id: string) => {
    const { data, error } = await supabase.from('profiles').select('*').eq('id', id).single();
    if (error) throw error;
    setProfile(data);
    if (!localStorage.getItem(`zenith:onboarded:${id}`)) setShowOnboarding(true);
  }, []);

  useEffect(() => {
    if (config.e2e) { const active = localStorage.getItem('zenith:e2e-session') === 'true'; if (active) { setUser({ id:demoProfile.id } as User); setProfile({ ...demoProfile, codename:localStorage.getItem('zenith:e2e-codename') ?? demoProfile.codename }); } setLoading(false); return; }
    void supabase.auth.getSession().then(({ data }) => { setUser(data.session?.user ?? null); if (data.session?.user) void loadProfile(data.session.user.id); setLoading(false); });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => { setUser(session?.user ?? null); if (session?.user) void loadProfile(session.user.id); else setProfile(null); });
    return () => listener.subscription.unsubscribe();
  }, [loadProfile]);

  const value = useMemo<AuthValue>(() => ({
    user, profile, loading, isGuest, showOnboarding,
    login: async (codename, password) => {
      if (config.e2e) { localStorage.setItem('zenith:e2e-session','true'); localStorage.setItem('zenith:e2e-codename',codename); setUser({ id:demoProfile.id } as User); setProfile({ ...demoProfile,codename }); return; }
      const { error } = await supabase.auth.signInWithPassword({ email:emailFor(codename), password }); if (error) throw error;
    },
    register: async (codename, password, invite) => {
      if (config.e2e) { if (!invite) throw new Error('Invalid invite code.'); localStorage.setItem('zenith:e2e-session','true'); localStorage.setItem('zenith:e2e-codename',codename); setUser({ id:demoProfile.id } as User); setProfile({ ...demoProfile,codename }); setShowOnboarding(true); return; }
      const checked = await supabase.rpc('check_invite', { p_code:invite }); if (checked.error || !checked.data) throw checked.error ?? new Error('Invalid invite code.');
      const existing = await supabase.from('profiles').select('id').eq('codename',codename).maybeSingle(); if (existing.error) throw existing.error; if (existing.data) throw new Error('Codename already exists.');
      const signed = await supabase.auth.signUp({ email:emailFor(codename), password }); if (signed.error || !signed.data.user) throw signed.error ?? new Error('Registration failed.');
      const created = await supabase.from('profiles').insert({ id:signed.data.user.id, codename, level:1 }).select().single(); if (created.error) throw created.error;
      const used = await supabase.rpc('use_invite', { p_code:invite }); if (used.error || !used.data) throw used.error ?? new Error('Invite could not be consumed.');
      setProfile(created.data); setShowOnboarding(true);
    },
    continueAsGuest: () => { setGuest(true); setProfile({ ...demoProfile, id:'guest', codename:'GUEST', level:1, fr_count:0 }); },
    finishOnboarding: () => { if (profile) localStorage.setItem(`zenith:onboarded:${profile.id}`,'true'); setShowOnboarding(false); },
    logout: async () => { if (!config.e2e) { const { error } = await supabase.auth.signOut(); if (error) throw error; } localStorage.removeItem('zenith:e2e-session'); setUser(null); setProfile(null); setGuest(false); setShowOnboarding(false); },
  }), [isGuest, loading, profile, showOnboarding, user]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
